import {
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Layout.css';
import { useRef, useState, useEffect } from 'react';
import Logo from '../assets/logo.svg';
import AvatarIcon from '../assets/avatar.svg';
import Menu from './Menu';
import PlusIcon from './PlusIcon';
import Footer from './Footer';


interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const isAuthPage = [
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
  ].includes(location.pathname);

  const isLandingPage = location.pathname === '/';

  const showFooter =
    !isAuthPage &&
    !['/welcome', '/properties/new'].includes(location.pathname);

  return (
    <div className={`layout ${isLandingPage ? 'layout-landing' : ''}`}>
      {/* Navigation Header */}
      {!isLandingPage && (
        <header className="layout-header">
          <nav className="layout-nav">
            <div className="nav-brand">
              <Link to="/" className="brand-link">
                <img src={Logo} alt="Valuera" className="brand-logo" />
              </Link>
            </div>

            <div className="nav-links">
              {isAuthenticated ? (
                // Authenticated user navigation
                <>
                  <Menu />
                  {user?.role === 'admin' && (
                    <Link to="/admin" className="nav-link">
                      Admin
                    </Link>
                  )}
                  <div>
                    <button
                      ref={anchorRef}
                      id="composition-button"
                      className="profile-menu-btn"
                      aria-controls={open ? 'composition-menu' : undefined}
                      aria-expanded={open ? 'true' : undefined}
                      aria-haspopup="true"
                      onClick={handleToggle}
                    >
                      <img
                        src={AvatarIcon}
                        alt="Profile"
                        className="profile-menu-icon"
                      />
                    </button>
                    <Popper
                      open={open}
                      anchorEl={anchorRef.current}
                      role={undefined}
                      placement="bottom-start"
                      transition
                      disablePortal
                    >
                      {({ TransitionProps, placement }) => (
                        <Grow
                          {...TransitionProps}
                          style={{
                            transformOrigin:
                              placement === 'bottom-start'
                                ? 'left top'
                                : 'left bottom',
                          }}
                        >
                          <Paper className="profile-dropdown-paper">
                            <ClickAwayListener onClickAway={handleClose}>
                               <MenuList
                                autoFocusItem={open}
                                id="composition-menu"
                                aria-labelledby="composition-button"
                                onKeyDown={handleListKeyDown}
                              >
                                <MenuItem
                                  onClick={handleClose}
                                  component={Link}
                                  to="/profile"
                                >
                                  Profile
                                </MenuItem>
                                <MenuItem 
                                  onClick={handleClose}
                                  component={Link}
                                  to="/shop"
                                >
                                  Subscription
                                </MenuItem>
                                <MenuItem
                                  onClick={(e) => {
                                    handleClose(e);
                                    logout();
                                  }}
                                  data-testid="logout-button"
                                >
                                  Logout
                                </MenuItem>
                              </MenuList>
                            </ClickAwayListener>
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                  </div>
                  <button
                    className="new-property-btn"
                    onClick={() => navigate('/properties/new')}
                  >
                    <PlusIcon className="new-property-icon" />
                    <span className="new-property-text">
                      Neue Immobilie hinzufügen
                    </span>
                  </button>
                </>
              ) : (
                // Unauthenticated user navigation
                !isAuthPage && (
                  <>
                    <Link to="/login" className="nav-link">
                      Login
                    </Link>
                    <Link to="/register" className="nav-link">
                      Register
                    </Link>
                  </>
                )
              )}
            </div>
          </nav>
        </header>
      )}

      {/* Main Content */}
      <main
        className={`layout-main ${isLandingPage ? 'layout-main-landing' : ''}`}
      >
        {children}
      </main>

      {showFooter && <Footer />}
    </div>
  );
};


export default Layout;
